import { useTranslations } from "@zapier/i18n";
import {
  ServiceIcon,
  Text,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { Connection } from "@zapier/client";
import { getServiceIconUrl } from "@zapier/utils";

import styles from "./ConvertToManagedPreviewCard.module.css";

interface Props {
  connection: Connection;
  title?: string;
  isManaged?: boolean;
}

/** Compact card showing a connection before or after conversion. */
export function ConvertToManagedPreviewCard({
  connection,
  title,
  isManaged,
}: Props) {
  const t = useTranslations("connections-convert-to-managed.modal");
  const appName = connection.selectedApi?.split("@")[0] ?? "";
  const iconSource =
    connection.implementation?.images?.url64X64 ?? getServiceIconUrl(appName);
  const accessIndicator = isManaged
    ? { icon: "miscSecurity" as const, label: t("labels.managed-connection") }
    : connection.isShared
      ? { icon: "personGroup" as const, label: t("labels.shared-connection") }
      : undefined;

  return (
    <div className={styles.card}>
      <ServiceIcon
        aria-hidden
        serviceName={appName}
        size="x-small"
        src={iconSource}
      />
      <Text type="Body3" className={styles.title}>
        {title ?? connection.title}
      </Text>
      {accessIndicator && (
        <Tooltip content={accessIndicator.label} placement="bottom">
          <TooltipFocusableTrigger>
            <Icon
              aria-label={accessIndicator.label}
              name={accessIndicator.icon}
              size={16}
              isBlock={true}
              color="TextWeaker"
            />
          </TooltipFocusableTrigger>
        </Tooltip>
      )}
      <Icon
        aria-label={t("labels.connected")}
        name="formCheckCircle"
        size={16}
        color="StatusSuccess"
      />
    </div>
  );
}
